import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useServer, useUpdateServer } from '../../hooks/useServers';
import { ServerForm, ServerFormData } from '../../components/server/ServerForm';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useToastStore } from '../../stores/toastStore';
import { getErrorMessage } from '../../lib/api/client';

export function EditServerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: server, isLoading, error } = useServer(id!);
  const updateServer = useUpdateServer();
  const { addToast } = useToastStore();

  const handleSubmit = async (data: ServerFormData) => {
    try {
      // Only include ssh_key if it was provided (not empty)
      const updateData = {
        ...data,
        ssh_key: data.ssh_key || undefined,
      };
      await updateServer.mutateAsync({ id: id!, data: updateData });
      addToast({ type: 'success', message: 'Server updated successfully' });
      navigate(`/servers/${id}`);
    } catch (error: unknown) {
      addToast({ type: 'error', message: getErrorMessage(error) });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-96 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !server) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-red-600">Server not found</p>
        <Button variant="link" onClick={() => navigate('/servers')}>
          Back to Servers
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/servers/${id}`)}
        className="gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Server
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Edit Server: {server.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <ServerForm
            server={server}
            onSubmit={handleSubmit}
            onCancel={() => navigate(`/servers/${id}`)}
            isLoading={updateServer.isPending}
          />
        </CardContent>
      </Card>
    </div>
  );
}
